import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { MFAProvider } from "@/contexts/MFAContext";
import { TimezoneProvider } from "@/contexts/TimezoneContext";
import { ProviderProvider } from "@/contexts/ProviderContext";
import { PrivateRoute } from "@/components/PrivateRoute";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { GoogleAnalyticsRouteTracker } from "@/components/GoogleAnalyticsRouteTracker";
import AttributionProfileCommitter from "@/components/AttributionProfileCommitter";

// Redirect component that preserves hash params (for Supabase invite flow)
// Uses window.location.replace for reliable hash preservation
const InviteRedirect = () => {
  React.useEffect(() => {
    // Preserve the hash fragment when redirecting
    const hash = window.location.hash;
    window.location.replace(`/auth${hash}`);
  }, []);

  // Show loading while redirecting
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>
  );
};

// Page Imports
import RootPage from "./pages/RootPage"; // New Root page for '/'
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PricingPage from "./pages/PricingPage";
import SelectTierPage from "./pages/SelectTierPage"; // Tier selection for new users
import ConnectJotformPage from "./pages/ConnectJotformPage"; // Jotform connection during onboarding
import FeaturesPage from "./pages/FeaturesPage"; // Import the new FeaturesPage
import JotformBackupPage from "./pages/JotformBackupPage";
import JotformRestorePage from "./pages/JotformRestorePage";
import JotformBackupSoftwarePage from "./pages/JotformBackupSoftwarePage";
import JotformFormRecoveryPage from "./pages/JotformFormRecoveryPage";
import AgenciesPage from "./pages/AgenciesPage"; // Campaign landing: agencies
import ItCompliancePage from "./pages/ItCompliancePage"; // Campaign landing: IT & compliance
import ResourceLandingPage from "./pages/ResourceLandingPage"; // Gated resources (scaffolded)
import ResourceThankYouPage from "./pages/ResourceThankYouPage"; // Gated resources thank-you
import ChangelogPage from "./pages/ChangelogPage";
import FormAssemblyCallbackPage from "./pages/FormAssemblyCallbackPage";
import FormDetailPage from "./pages/FormDetailPage";
import AboutPage from "./pages/AboutPage"; // New
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage"; // New
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage"; // New
import CustomerTermsPage from "./pages/CustomerTermsPage"; // Customer Terms (logged in only)
import DashboardPage from "./pages/DashboardPage"; // New for logged-in users (was Index.tsx content)
import AnalyticsDebugHarness from "./pages/AnalyticsDebugHarness";
import HelpPage from "./pages/HelpPage"; // Help system
import MFASetupPage from "./pages/MFASetupPage"; // MFA enrollment
import MFAVerifyPage from "./pages/MFAVerifyPage"; // MFA verification
import AccountSettingsPage from "./pages/AccountSettingsPage"; // Account settings
import JotformFormDetailPage from "./pages/JotformFormDetailPage"; // Jotform form detail
import Welcome from "./pages/Welcome"; // Post-signup conversion page
import PostCheckout from "./pages/PostCheckout"; // Post-Stripe-checkout router
import TrialStarted from "./pages/TrialStarted"; // Trial-started confirmation
import PurchaseSuccess from "./pages/PurchaseSuccess"; // Paid purchase confirmation
import NotFound from "./pages/NotFound";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MFAProvider>
        <TimezoneProvider>
        <ProviderProvider>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <GoogleAnalyticsRouteTracker />
              <AttributionProfileCommitter />
              <Routes>
              <Route path="/" element={<RootPage />} /> {/* RootPage handles landing/dashboard based on auth */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              {/* Supabase invite redirects to /auth with hash preserved */}
              <Route path="/auth/invite" element={<InviteRedirect />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/features" element={<FeaturesPage />} /> {/* Add route for FeaturesPage */}
              <Route path="/jotform-backup" element={<JotformBackupPage />} />
              <Route path="/jotform-restore" element={<JotformRestorePage />} />
              <Route path="/jotform-backup-software" element={<JotformBackupSoftwarePage />} />
              <Route path="/jotform-form-recovery" element={<JotformFormRecoveryPage />} />
              {/* Campaign landing pages (public) */}
              <Route path="/agencies" element={<AgenciesPage />} />
              <Route path="/it-compliance" element={<ItCompliancePage />} />
              {/* Gated resources — scaffolded, unpublished (see src/config/resources.ts) */}
              <Route
                path="/resources/jotform-agency-form-safety-playbook"
                element={<ResourceLandingPage assetId="agency_playbook" />}
              />
              <Route
                path="/resources/jotform-agency-form-safety-playbook/thank-you"
                element={<ResourceThankYouPage assetId="agency_playbook" />}
              />
              <Route
                path="/resources/it-saas-backup-checklist"
                element={<ResourceLandingPage assetId="it_checklist" />}
              />
              <Route
                path="/resources/it-saas-backup-checklist/thank-you"
                element={<ResourceThankYouPage assetId="it_checklist" />}
              />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/help/:articleSlug" element={<HelpPage />} />
              {import.meta.env.DEV ? (
                <Route path="/dev/analytics-harness" element={<AnalyticsDebugHarness />} />
              ) : null}

              {/* Protected routes */}
              <Route element={<PrivateRoute />}>
                {/* MFA pages - require auth but bypass MFA check (handled internally) */}
                <Route path="/mfa/setup" element={<MFASetupPage />} />
                <Route path="/mfa/verify" element={<MFAVerifyPage />} />
                {/* Onboarding: Welcome page - fires sign_up conversion once per user */}
                <Route path="/welcome" element={<Welcome />} />
                {/* Onboarding: Jotform connection - requires auth + MFA but bypasses connection/tier check */}
                <Route path="/connect-jotform" element={<ConnectJotformPage />} />
                {/* Tier selection page - requires auth + MFA + Jotform but bypasses tier check */}
                <Route path="/select-tier" element={<SelectTierPage />} />
                {/* Post-checkout routing + conversion pages */}
                <Route path="/post-checkout" element={<PostCheckout />} />
                <Route path="/trial-started" element={<TrialStarted />} />
                <Route path="/purchase-success" element={<PurchaseSuccess />} />
                {/* DashboardPage is now implicitly protected by RootPage's logic,
                    but explicit PrivateRoute could be added if /dashboard is a direct link */}
                <Route path="/dashboard" element={<DashboardPage />} /> {/* Explicit route for dashboard */}
                <Route path="/customer-terms" element={<CustomerTermsPage />} /> {/* Customer Terms (logged in only) */}
                <Route path="/account" element={<AccountSettingsPage />} /> {/* Account settings with MFA */}
                <Route path="/formassembly" element={<Navigate to="/dashboard" replace />} />
                <Route path="/formassembly/:formId" element={<FormDetailPage />} />
                <Route path="/api/formassembly/callback" element={<FormAssemblyCallbackPage />} />
                <Route path="/jotform/:formId" element={<JotformFormDetailPage />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
        </ProviderProvider>
        </TimezoneProvider>
      </MFAProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
